import { useEffect, useState } from "react";
import getUser from "src/models/User";
import { CvQuota, requestQuotaStatus } from "src/content/pages/cv-builder/ai";

export type ToolPlan = "pro" | "free";

const STORAGE_PREFIX = "tool-plan:";
const CHANGE_EVENT = "tool-plan-change";

let pending: Promise<void> | null = null;
/** Satu percobaan per muat halaman, agar 4xx (aksi `status` belum ada di BE) tidak diulang tiap render. */
let attempted = false;

function storageKey() {
	if (!localStorage.getItem("TOKEN")) return null;
	const id = getUser()?.id;
	return id != null ? `${STORAGE_PREFIX}${id}` : null;
}

export function readToolPlan(): ToolPlan | null {
	const key = storageKey();
	if (!key) return null;
	try {
		const value = sessionStorage.getItem(key);
		return value === "pro" || value === "free" ? value : null;
	} catch {
		return null;
	}
}

/** Kuota tamu tidak disimpan: chip hanya untuk user login. */
export function rememberToolPlan(quota: CvQuota | null) {
	const key = storageKey();
	if (!key || !quota) return;
	const plan: ToolPlan = quota.unlimited ? "pro" : "free";
	try {
		if (sessionStorage.getItem(key) === plan) return;
		sessionStorage.setItem(key, plan);
	} catch {
		return;
	}
	window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function fetchToolPlan(): Promise<void> {
	if (pending) return pending;
	if (attempted || !storageKey()) return Promise.resolve();
	attempted = true;
	pending = requestQuotaStatus()
		.then(rememberToolPlan)
		.catch(() => undefined)
		.finally(() => {
			pending = null;
		});
	return pending;
}

export function resetToolPlanRequest() {
	pending = null;
	attempted = false;
}

export function useToolPlan(): ToolPlan | null {
	const [plan, setPlan] = useState<ToolPlan | null>(readToolPlan);

	useEffect(() => {
		const sync = () => setPlan(readToolPlan());
		window.addEventListener(CHANGE_EVENT, sync);
		if (!readToolPlan()) fetchToolPlan();
		return () => window.removeEventListener(CHANGE_EVENT, sync);
	}, []);

	return plan;
}
